// src/components/ThirdPartyContact/ThirdPartyContact.js
import React, { useState, useEffect, useRef } from "react";
import { fetchUsers } from "../../store/Users/usersThunks";
import Box from "@mui/material/Box";
import SideModal from "../../components/SideModal";
import ThirdPartyContactForm from "./Components/ThirdPartyContactForm/ThirdPartyContactForm";
import ThirdPartyContactDataGrid from "./Components/ThirdPartyContactDataGrid/ThirdPartyContactDataGrid";
import { useDispatch } from "react-redux";
import {
  addThirdPartyContact,
  updateThirdPartyContact,
  fetchThirdPartyContacts,
} from "../../store/ThirdPartyContacts/thirdPartyContactsThunks";
import { Paper, useTheme, useMediaQuery } from "@mui/material";
import PageTitle from "../../components/PageTitle";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import ThirdPartyContactCustomToolbar from "./Components/ThirdPartyContactDataGrid/ThirdPartyContactCustomToolbar";

function ThirdPartyContact() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const gridApiRef = useRef(null);

  // When the form modal is open, fetch supervisor users for the select
  useEffect(() => {
    if (open) {
      dispatch(
        fetchUsers({
          filters: [{ field: "role", operator: "equals", value: "supervisor" }],
        })
      );
    }
  }, [open, dispatch]);

  const handleAddContact = () => {
    setSelectedContact(null);
    setOpen(true);
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setOpen(true);
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedContact) {
        await dispatch(
          updateThirdPartyContact({ id: selectedContact.id, ...formData })
        );
      } else {
        await dispatch(addThirdPartyContact(formData));
      }
      await dispatch(fetchThirdPartyContacts({ page: 1 }));
      setOpen(false);
      setActiveStep(0);
      setSelectedContact(null);
    } catch (error) {
      console.error("Error submitting contact:", error);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    dispatch(fetchThirdPartyContacts({ page: 1, search: query }));
  };

  const handleRefresh = () => {
    dispatch(fetchThirdPartyContacts({ page: 1, search: searchQuery }));
  };

  const handleFilterClick = () => {
    if (gridApiRef.current) {
      gridApiRef.current.showFilterPanel();
    }
  };

  const handleColumnClick = () => {
    if (gridApiRef.current) {
      gridApiRef.current.showPreferences('columns');
    }
  };

  return (
    <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      maxHeight: "100vh",
      overflow: "hidden",
      backgroundColor: (theme) => theme.palette.background.default,
    }}
    >
      {/* Fixed header section */}
      <Box
        sx={{
          backgroundColor: (theme) => theme.palette.background.paper,
          p: isSmallScreen ? 1 : 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box 
          sx={{ 
            display: "flex", 
            flexDirection: isSmallScreen ? "column" : "row",
            alignItems: isSmallScreen ? "flex-start" : "center", 
            justifyContent: "space-between",
            gap: isSmallScreen ? 2 : 0
          }}
        >
          <PageTitle 
            title="Third Party Contacts" 
            subtitle="Manage external contacts and their information"
          />
          <Box 
            sx={{ 
              display: "flex", 
              flexDirection: isSmallScreen ? "column" : "row",
              alignItems: isSmallScreen ? "stretch" : "center", 
              gap: 2,
              width: isSmallScreen ? "100%" : "auto"
            }}
          >
            <ThirdPartyContactCustomToolbar 
              onSearch={handleSearch}
              searchQuery={searchQuery}
              onRefresh={handleRefresh}
              onFilterClick={handleFilterClick}
              onColumnClick={handleColumnClick}
              isSmallScreen={isSmallScreen}
            />
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddContact}
              variant="contained"
              size="small"
              fullWidth={isSmallScreen}
              sx={{
                backgroundColor: (theme) => theme.palette.background.button,
                color: (theme) => theme.palette.common.white,
                textTransform: 'none',
                px: 2,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.background.button,
                },
              }}
            >
              Add Contact
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Content section */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          position: "relative",
          height: "calc(100vh - 120px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "auto",
            "& .MuiDataGrid-root": {
              border: "none",
              backgroundColor: (theme) => theme.palette.background.paper,
              padding: 0,
              margin: 0,
            },
          }}
        >
          <ThirdPartyContactDataGrid
            onEditContact={handleEditContact}
            onAddContact={handleAddContact}
            searchQuery={searchQuery}
            onSearch={handleSearch}
            apiRef={gridApiRef}
          />
        </Paper>
      </Box>

      <SideModal
        open={open}
        onClose={() => {
          setOpen(false);
          setActiveStep(0);
          setSelectedContact(null);
        }}
        title={selectedContact ? "Edit Contact" : "Add New Contact"}
      >
        <ThirdPartyContactForm
          onSubmit={handleSubmit}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          initialData={selectedContact}
        />
      </SideModal>
    </Box>
  );
}

export default ThirdPartyContact;
